import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  DeleteForever as DeleteIcon,
  Storage as StorageIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import axios from '../api/axios';

function DatabaseManagement() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '' });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleExportDatabase = async () => {
    try {
      setLoading(true);
      setError('');
      setMessage('');

      const response = await axios.get('/database/export', {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/sql' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'database_dump.sql';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setMessage('База данных успешно экспортирована');
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка экспорта базы данных');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.sql')) {
        setError('Выберите файл с расширением .sql');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setError('');
      setMessage('');
    }
  };

  const handleImportDatabase = async () => {
    if (!selectedFile) {
      setError('Выберите файл для импорта');
      return;
    }

    setConfirmDialog({ open: false, type: '' });

    try {
      setLoading(true);
      setError('');
      setMessage('');
      setUploadProgress(0);

      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post('/database/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });

      setMessage(response.data.message || 'База данных успешно импортирована');
      setSelectedFile(null);

      // Reload page after 2 seconds to refresh data
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка импорта базы данных');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const handleClearDatabase = async () => {
    setConfirmDialog({ open: false, type: '' });

    try {
      setLoading(true);
      setError('');
      setMessage('');

      const response = await axios.post('/database/clear');
      setMessage(response.data.message || 'База данных успешно очищена');

      // Reload page after 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка очистки базы данных');
    } finally {
      setLoading(false);
    }
  };

  const openConfirmDialog = (type) => {
    setConfirmDialog({ open: true, type });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({ open: false, type: '' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <StorageIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
        <Typography variant="h4">
          Управление базой данных
        </Typography>
      </Box>

      {message && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMessage('')}>
          <AlertTitle>Успешно</AlertTitle>
          {message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          <AlertTitle>Ошибка</AlertTitle>
          {error}
        </Alert>
      )}

      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Внимание!</AlertTitle>
        Эта страница предназначена только для администраторов. Операции с базой данных могут привести к потере данных. Перед импортом или очисткой обязательно создайте резервную копию.
      </Alert>

      {/* Export Database */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DownloadIcon sx={{ mr: 1, color: 'success.main' }} />
            <Typography variant="h6">
              Экспорт базы данных
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Создать резервную копию всей базы данных в формате SQL. Файл будет содержать все таблицы, данные и структуру.
          </Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExportDatabase}
            disabled={loading}
          >
            Скачать дамп базы данных
          </Button>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* Import Database */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <UploadIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Импорт базы данных
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Загрузить дамп базы данных из SQL файла. ВНИМАНИЕ: Это заменит все текущие данные!
          </Typography>

          <Box sx={{ mb: 2 }}>
            <input
              accept=".sql"
              style={{ display: 'none' }}
              id="raised-button-file"
              type="file"
              onChange={handleFileSelect}
            />
            <label htmlFor="raised-button-file">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                disabled={loading}
              >
                Выбрать SQL файл
              </Button>
            </label>
            {selectedFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Выбран файл: <strong>{selectedFile.name}</strong>
              </Typography>
            )}
          </Box>

          {uploadProgress > 0 && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Загрузка: {uploadProgress}%
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={() => openConfirmDialog('import')}
            disabled={!selectedFile || loading}
          >
            Импортировать базу данных
          </Button>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      {/* Clear Database */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DeleteIcon sx={{ mr: 1, color: 'error.main' }} />
            <Typography variant="h6" color="error">
              Очистка базы данных
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Удалить все данные из всех таблиц базы данных. Структура таблиц останется нетронутой. ВНИМАНИЕ: Это действие необратимо!
          </Typography>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Опасная операция!</AlertTitle>
            Это действие удалит ВСЕ данные из базы. Восстановление возможно только из резервной копии.
          </Alert>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => openConfirmDialog('clear')}
            disabled={loading}
          >
            Очистить базу данных
          </Button>
        </CardContent>
      </Card>

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Confirmation Dialogs */}
      <Dialog open={confirmDialog.open} onClose={closeConfirmDialog}>
        <DialogTitle>
          {confirmDialog.type === 'import' ? 'Подтверждение импорта' : 'Подтверждение очистки'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <WarningIcon sx={{ color: 'error.main', mr: 1, fontSize: 40 }} />
            <DialogContentText>
              {confirmDialog.type === 'import'
                ? 'Вы уверены, что хотите импортировать базу данных? Все текущие данные будут заменены данными из файла!'
                : 'Вы уверены, что хотите очистить базу данных? Все данные будут удалены безвозвратно!'
              }
            </DialogContentText>
          </Box>
          <Alert severity="warning">
            Это действие необратимо! Убедитесь, что у вас есть резервная копия.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirmDialog} color="inherit">
            Отмена
          </Button>
          <Button
            onClick={confirmDialog.type === 'import' ? handleImportDatabase : handleClearDatabase}
            color="error"
            variant="contained"
            autoFocus
          >
            Подтвердить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DatabaseManagement;
